import { useState, useEffect } from 'react';
import { X, Calendar, ArrowRight, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useFiscalYear } from '../../hooks/useFiscalYear';

interface OnboardingBannerProps {
  userName?: string;
}

export function OnboardingBanner({ userName }: OnboardingBannerProps) {
  const [dismissed, setDismissed] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const { fiscalYears, loading } = useFiscalYear();
  const navigate = useNavigate();

  // Vérifier si la bannière a déjà été fermée dans cette session
  useEffect(() => {
    const wasDismissed = sessionStorage.getItem('onboarding_dismissed');
    if (wasDismissed) {
      setDismissed(true);
    }
  }, []);

  // Afficher la bannière seulement si pas d'exercice fiscal et pas déjà fermée
  useEffect(() => {
    if (!loading && fiscalYears.length === 0 && !dismissed) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [loading, fiscalYears, dismissed]);

  const handleDismiss = () => {
    setDismissed(true);
    setIsVisible(false);
    sessionStorage.setItem('onboarding_dismissed', 'true');
  };

  const handleCreateFiscalYear = () => {
    navigate('/fiscal-years');
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="mb-6 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent dark:from-primary/20 dark:via-primary/10 dark:to-transparent border border-primary/20 rounded-xl p-5 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-1/4 w-24 h-24 bg-primary/5 rounded-full translate-y-1/2" />
      
      <button
        onClick={handleDismiss}
        className="absolute top-3 right-3 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        title="Fermer"
      >
        <X className="w-5 h-5" />
      </button>

      <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Bienvenue{userName ? `, ${userName}` : ''} ! 👋
            </h3>
          </div>
          <p className="text-gray-600 dark:text-gray-400 text-sm md:text-base">
            Pour commencer à utiliser toutes les fonctionnalités de Wanzo Compta, 
            créez votre premier exercice fiscal. Cela vous permettra d'enregistrer 
            vos opérations comptables et de générer vos rapports financiers.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleDismiss}
            className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
          >
            Plus tard
          </button>
          <button
            onClick={handleCreateFiscalYear}
            className="flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors shadow-sm"
          >
            <Calendar className="w-4 h-4" />
            <span>Créer un exercice</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
